import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {State} from 'react-native-gesture-handler';

import {
  GetAccountAffiliateDetailsOpts,
  InitiateExchangeCurrencyOpts,
  InitiateMatchTierOpts,
} from 'services/api/affiliate';
import {APICallState, ResponseError} from 'services/api/api';
import {AuthActions} from 'store/auth/auth.reducer';

import {PARTNERS, PARTNER_TIER_BRACKETS} from './partners';

export interface Partner {
  id: string;
  name: string;
  pointName: string;
  displayAsCredits: boolean;
  leapProperties: LeapProperty[];
  isLinked?: boolean;
  affiliateaccount?: string;
}

export interface LeapProperty {
  code: string;
  outletCode: string;
  name: string;
  logo: string;
}

export interface AffiliateAccount {
  partnerId: string;
  affiliateAccount: string;
}

export interface AffiliateAccountDetails {
  partnerId: string;
  accountId: string;
  linkedRewardsAccount: string;
  homeProperty: string;
  loyaltyBalance: number;
  tierCode: string;
  tierScore: number;
  rewardCreditBalance: number;
  rewardCreditsToCurrencyConversionRate: number;
  currencyToRewardCreditsConversionRate: number;
  hasCrossPlay: boolean;
  lastQueryTime: Date | undefined;
}

export interface AccountCallStatus {
  getAccountDetails: APICallState;
  initiateMatchTier: APICallState;
  initiateMatchTierResponseError?: ResponseError;
}

export interface AccountDetailsStateMap {
  [partnerId: string]: AccountCallStatus;
}

export interface AffiliateAccountsObject {
  [partnerId: string]: AffiliateAccountDetails;
}

export interface TierBrackets {
  [tierLevel: string]: {
    minCredits?: number;
    maxCredits?: number;
    name: string;
  };
}
export interface PartnerTierBracketsMap {
  [partnerId: string]: TierBrackets;
}

export interface MatchStatusFailure extends ResponseError {
  partnerId: string;
}

export interface AffiliateState {
  readonly affiliateAccountList?: AffiliateAccount[];
  readonly getAccountAffiliateMappingsState: APICallState;
  readonly initiateExchangeCurrencyState: APICallState;
  readonly initiateExchangeCurrencyError: string;
  readonly affiliateAccountListMap?: AffiliateAccountsObject;
  readonly partners: Partner[];
  readonly getAccountDetailsStateMap: AccountDetailsStateMap;
  readonly partnerTierBrackets: PartnerTierBracketsMap;
}

export const initialState: AffiliateState = {
  affiliateAccountList: undefined,
  getAccountAffiliateMappingsState: APICallState.INITIAL,
  initiateExchangeCurrencyState: APICallState.INITIAL,
  affiliateAccountListMap: undefined,
  partners: PARTNERS,
  getAccountDetailsStateMap: {},
  partnerTierBrackets: PARTNER_TIER_BRACKETS,
  initiateExchangeCurrencyError: '',
};

const affiliateSlice = createSlice({
  name: 'affiliate',
  initialState,
  reducers: {
    getAffiliateAccountList(state) {
      state.getAccountAffiliateMappingsState = APICallState.LOADING;
    },
    getAffiliateAccountListSuccess(
      state,
      action: PayloadAction<AffiliateAccount[]>
    ) {
      state.affiliateAccountList = action.payload;
      action.payload.forEach(account => {
        const partner = state.partners.find(p =>
          p.leapProperties.some(lp => lp.code === account.partnerId)
        );
        if (partner) {
          partner.isLinked = true;
          partner.affiliateaccount = account.affiliateAccount;
        }
      });
      state.getAccountAffiliateMappingsState = APICallState.SUCCESS;
    },
    getAffiliateAccountListFailure(state) {
      state.getAccountAffiliateMappingsState = APICallState.ERROR;
    },
    initiateExchangeCurrency(
      state,
      _action: PayloadAction<InitiateExchangeCurrencyOpts>
    ) {
      state.initiateExchangeCurrencyState = APICallState.LOADING;
      state.initiateExchangeCurrencyError = '';
    },
    initiateExchangeCurrencySuccess(state) {
      state.initiateExchangeCurrencyState = APICallState.SUCCESS;
    },
    initiateExchangeCurrencyFailure(state, action: PayloadAction<string>) {
      state.initiateExchangeCurrencyState = APICallState.ERROR;
      state.initiateExchangeCurrencyError = action.payload;
    },
    initiateExchangeCurrencyReset(state) {
      state.initiateExchangeCurrencyState = APICallState.INITIAL;
    },
    getAffiliateAccountDetails(
      state,
      action: PayloadAction<GetAccountAffiliateDetailsOpts>
    ) {
      state.getAccountDetailsStateMap[action.payload.affiliateid] = {
        getAccountDetails: APICallState.LOADING,
        initiateMatchTier: APICallState.INITIAL,
      };
    },
    getAffiliateAccountDetailsSuccess(
      state,
      action: PayloadAction<AffiliateAccountDetails>
    ) {
      const {partnerId} = action.payload;
      if (!state.affiliateAccountListMap) {
        state.affiliateAccountListMap = {};
      }
      state.affiliateAccountListMap[partnerId] = action.payload;
      state.getAccountDetailsStateMap[partnerId].getAccountDetails =
        APICallState.SUCCESS;
    },
    getAffiliateAccountDetailsFailure(state, action: PayloadAction<string>) {
      state.getAccountDetailsStateMap[action.payload].getAccountDetails =
        APICallState.ERROR;
    },
    initiateMatchTier(state, action: PayloadAction<InitiateMatchTierOpts>) {
      const {partnerID} = action.payload;
      if (state.getAccountDetailsStateMap[partnerID]) {
        state.getAccountDetailsStateMap[partnerID].initiateMatchTier =
          APICallState.LOADING;
      } else {
        state.getAccountDetailsStateMap[partnerID] = {
          initiateMatchTier: APICallState.LOADING,
          getAccountDetails: APICallState.INITIAL,
        };
      }
    },
    initiateMatchTierSuccess(state, action: PayloadAction<string>) {
      state.getAccountDetailsStateMap[action.payload].initiateMatchTier =
        APICallState.SUCCESS;
    },
    initiateMatchTierFailure(state, action: PayloadAction<MatchStatusFailure>) {
      const accountCallStatus =
        state.getAccountDetailsStateMap[action.payload.partnerId];
      accountCallStatus.initiateMatchTier = APICallState.ERROR;
      accountCallStatus.initiateMatchTierResponseError = {
        resultCode: action.payload.resultCode,
        resultMessage: action.payload.resultMessage,
      };
    },
    initiateMatchTierReset(state, action: PayloadAction<string>) {
      const partnerId = action.payload;
      if (!state.getAccountDetailsStateMap[partnerId]) {
        return;
      }
      state.getAccountDetailsStateMap[action.payload].initiateMatchTier =
        APICallState.INITIAL;
      state.getAccountDetailsStateMap[
        action.payload
      ].initiateMatchTierResponseError = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(AuthActions.logout.type, () => initialState);
  },
});

export const AffiliateActions = affiliateSlice.actions;

const AffiliateReducer = affiliateSlice.reducer;
export default AffiliateReducer;
